import React, { useState, useEffect } from "react";
import {
  Button,
  FormControl,
  Grid2 as Grid,
  OutlinedInput,
  Select,
  MenuItem,
  ListItemText,
  Box,
  Chip,
  Typography,
  Paper,
  InputAdornment,
  ToggleButtonGroup,
  ToggleButton,
  Tooltip,
  IconButton,
  Popover,
  TextField,
} from "@mui/material";
import {
  Search as SearchIcon,
  Tune as TuneIcon,
  RestartAlt as ResetIcon,
  Refresh as RefreshIcon,
  FileDownload as ExportIcon,
} from "@mui/icons-material";

/**
 * 채팅 로그 필터 Props
 */
export interface ChatLogFilterProps {
  /**
   * 현재 필터 값들
   */
  values: {
    search: string;
    sessionType: string;
    sessionUserType: string;
    status: string;
    startDate: string;
    endDate: string;
    feedbackFilter: string;
  };
  /**
   * 로딩 상태
   */
  loading?: boolean;
  /**
   * 필터 변경 이벤트
   */
  onChange: (values: {
    search: string;
    sessionType: string;
    sessionUserType: string;
    status: string;
    startDate: string;
    endDate: string;
    feedbackFilter: string;
  }) => void;
  /**
   * 필터 초기화 이벤트
   */
  onReset: () => void;
  /**
   * 새로고침 이벤트
   */
  onRefresh: () => void;
  /**
   * 엑셀 내보내기 이벤트
   */
  onExport: () => void;
}

/**
 * 채팅 로그 필터 컴포넌트
 *
 * @description 채팅 로그 검색 및 필터링을 위한 컴포넌트 (SuperChatContent Filter 스타일 적용)
 * @param props ChatLogFilterProps
 * @returns JSX.Element
 * @author Max <sud665@ymjlab.com>
 * @version 2.0.0
 * @since 2025-01-16
 */
export const Filter = (props: ChatLogFilterProps) => {
  const {
    values,
    loading = false,
    onChange,
    onReset,
    onRefresh,
    onExport,
  } = props;

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [searchFocused, setSearchFocused] = useState(false);
  const [formValues, setFormValues] = useState({
    search: values.search || "",
    sessionType: values.sessionType || "",
    sessionUserType: values.sessionUserType || "",
    status: values.status || "",
    startDate: values.startDate || "",
    endDate: values.endDate || "",
    feedbackFilter: values.feedbackFilter || "",
  });

  // props가 변경될 때 formValues 업데이트
  useEffect(() => {
    setFormValues({
      search: values.search || "",
      sessionType: values.sessionType || "",
      sessionUserType: values.sessionUserType || "",
      status: values.status || "",
      startDate: values.startDate || "",
      endDate: values.endDate || "",
      feedbackFilter: values.feedbackFilter || "",
    });
  }, [values]);

  const handleOpenFilters = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseFilters = () => {
    setAnchorEl(null);
  };

  const handleApplyFilters = () => {
    onChange(formValues);
    handleCloseFilters();
  };

  const handleSearch = () => {
    onChange(formValues);
  };

  const handleReset = () => {
    const defaultValues = {
      search: "",
      sessionType: "",
      sessionUserType: "",
      status: "",
      startDate: "",
      endDate: "",
      feedbackFilter: "",
    };
    setFormValues(defaultValues);
    onReset();
    handleCloseFilters();
  };

  const handleSearchChange = (value: string) => {
    setFormValues({ ...formValues, search: value });
  };

  const handleSessionTypeChange = (value: string) => {
    setFormValues({ ...formValues, sessionType: value });
  };

  const handleSessionUserTypeChange = (value: string) => {
    setFormValues({ ...formValues, sessionUserType: value });
  };

  const handleStatusChange = (value: string) => {
    setFormValues({ ...formValues, status: value });
  };

  const handleStartDateChange = (value: string) => {
    setFormValues({ ...formValues, startDate: value });
  };

  const handleEndDateChange = (value: string) => {
    setFormValues({ ...formValues, endDate: value });
  };

  const handleFeedbackFilterChange = (value: string) => {
    setFormValues({ ...formValues, feedbackFilter: value });
  };

  const open = Boolean(anchorEl);
  const id = open ? "filters-popover" : undefined;

  // 세션 타입 옵션
  const sessionTypeOptions = [
    { value: "QNA", label: "QNA 채팅" },
    { value: "TRIAL", label: "체험 상담" },
    { value: "GENERAL", label: "일반 문의" },
  ];

  // 사용자 타입 옵션
  const sessionUserTypeOptions = [
    { value: "GUEST", label: "게스트" },
    { value: "MEMBER", label: "회원" },
    { value: "ADMIN", label: "관리자" },
  ];

  // 상태 옵션
  const statusOptions = [
    { value: "ACTIVE", label: "활성" },
    { value: "COMPLETED", label: "완료" },
    { value: "PAUSED", label: "일시정지" },
    { value: "TERMINATED", label: "종료" },
  ];

  // 피드백 필터 옵션
  const feedbackFilterOptions = [
    { value: "has_feedback", label: "피드백 있음" },
    { value: "no_feedback", label: "피드백 없음" },
  ];

  // 표시 이름 생성 함수들
  const getSessionTypeDisplayName = (type: string) => {
    const option = sessionTypeOptions.find((opt) => opt.value === type);
    return option ? option.label : null;
  };

  const getSessionUserTypeDisplayName = (type: string) => {
    const option = sessionUserTypeOptions.find((opt) => opt.value === type);
    return option ? option.label : null;
  };

  const getStatusDisplayName = (status: string) => {
    const option = statusOptions.find((opt) => opt.value === status);
    return option ? option.label : null;
  };

  const getFeedbackFilterDisplayName = (filter: string) => {
    const option = feedbackFilterOptions.find((opt) => opt.value === filter);
    return option ? option.label : null;
  };

  // 활성화된 필터 태그 생성
  const renderFilterTags = () => {
    const tags = [];

    if (formValues.search) {
      tags.push(
        <Chip
          key="search"
          label={`검색어: ${formValues.search}`}
          size="small"
          onDelete={() => handleSearchChange("")}
          sx={{ mr: 1, mb: 1 }}
        />
      );
    }

    if (formValues.sessionType) {
      const displayName = getSessionTypeDisplayName(formValues.sessionType);
      if (displayName) {
        tags.push(
          <Chip
            key="sessionType"
            label={`세션 타입: ${displayName}`}
            size="small"
            onDelete={() => handleSessionTypeChange("")}
            sx={{ mr: 1, mb: 1 }}
          />
        );
      }
    }

    if (formValues.sessionUserType) {
      const displayName = getSessionUserTypeDisplayName(
        formValues.sessionUserType
      );
      if (displayName) {
        tags.push(
          <Chip
            key="sessionUserType"
            label={`사용자 타입: ${displayName}`}
            size="small"
            onDelete={() => handleSessionUserTypeChange("")}
            sx={{ mr: 1, mb: 1 }}
          />
        );
      }
    }

    if (formValues.status) {
      const displayName = getStatusDisplayName(formValues.status);
      if (displayName) {
        tags.push(
          <Chip
            key="status"
            label={`상태: ${displayName}`}
            size="small"
            onDelete={() => handleStatusChange("")}
            sx={{ mr: 1, mb: 1 }}
          />
        );
      }
    }

    if (formValues.startDate) {
      tags.push(
        <Chip
          key="startDate"
          label={`시작일: ${formValues.startDate}`}
          size="small"
          onDelete={() => handleStartDateChange("")}
          sx={{ mr: 1, mb: 1 }}
        />
      );
    }

    if (formValues.endDate) {
      tags.push(
        <Chip
          key="endDate"
          label={`종료일: ${formValues.endDate}`}
          size="small"
          onDelete={() => handleEndDateChange("")}
          sx={{ mr: 1, mb: 1 }}
        />
      );
    }

    if (formValues.feedbackFilter) {
      const displayName = getFeedbackFilterDisplayName(
        formValues.feedbackFilter
      );
      if (displayName) {
        tags.push(
          <Chip
            key="feedbackFilter"
            label={`피드백: ${displayName}`}
            size="small"
            onDelete={() => handleFeedbackFilterChange("")}
            sx={{ mr: 1, mb: 1 }}
          />
        );
      }
    }

    return tags.length > 0 ? (
      <Box sx={{ display: "flex", flexWrap: "wrap", mt: 2 }}>
        {tags}
        {tags.length > 0 && (
          <Chip
            label="모두 지우기"
            size="small"
            variant="outlined"
            onClick={handleReset}
            deleteIcon={<ResetIcon />}
            sx={{ mb: 1 }}
          />
        )}
      </Box>
    ) : null;
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 1,
        mb: 3,
      }}
    >
      {/* 메인 검색바 */}
      <Grid container spacing={2} alignItems="center">
        <Grid size={{ xs: 12, sm: 6, md: 8 }}>
          <FormControl fullWidth variant="outlined" size="small">
            <OutlinedInput
              placeholder="세션 ID, 사용자명으로 검색..."
              value={formValues.search || ""}
              onChange={(event) => {
                handleSearchChange(event.target.value);
              }}
              onFocus={() => setSearchFocused(true)}
              onBlur={() => setSearchFocused(false)}
              startAdornment={
                <InputAdornment position="start">
                  <SearchIcon color={searchFocused ? "primary" : "action"} />
                </InputAdornment>
              }
              sx={{
                transition: "all 0.2s",
                boxShadow: searchFocused ? 1 : 0,
                "&.Mui-focused": {
                  borderColor: "primary.main",
                },
              }}
            />
          </FormControl>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 4 }} sx={{ display: "flex", gap: 1 }}>
          <Tooltip title="고급 필터 옵션">
            <IconButton
              color="primary"
              onClick={handleOpenFilters}
              aria-describedby={id}
              sx={{
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1,
                flex: 1,
              }}
            >
              <TuneIcon />
            </IconButton>
          </Tooltip>

          <Button
            variant="contained"
            disableElevation
            onClick={handleSearch}
            sx={{ flex: 2 }}
          >
            검색
          </Button>

          <Tooltip title="새로고침">
            <IconButton
              onClick={onRefresh}
              disabled={loading}
              sx={{
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1,
              }}
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>

          <Button
            variant="outlined"
            startIcon={<ExportIcon />}
            onClick={onExport}
            disabled={loading}
            sx={{ flex: 2 }}
          >
            Excel
          </Button>
        </Grid>
      </Grid>

      {/* 선택된 필터 태그 */}
      {renderFilterTags()}

      {/* 고급 필터 팝오버 */}
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleCloseFilters}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        PaperProps={{
          sx: {
            width: 380,
            p: 2,
            mt: 1,
          },
        }}
      >
        <Typography variant="subtitle2" fontWeight={600} gutterBottom>
          고급 필터
        </Typography>

        {/* 세션 타입 */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" color="text.secondary" gutterBottom>
            세션 타입
          </Typography>
          <FormControl fullWidth variant="outlined" size="small">
            <Select
              value={formValues.sessionType || ""}
              onChange={(event) => {
                handleSessionTypeChange(event.target.value);
              }}
              displayEmpty
              sx={{ mt: 0.5 }}
            >
              <MenuItem value="">
                <ListItemText primary="전체" />
              </MenuItem>
              {sessionTypeOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  <ListItemText primary={option.label} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* 사용자 타입 */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" color="text.secondary" gutterBottom>
            사용자 타입
          </Typography>
          <ToggleButtonGroup
            value={formValues.sessionUserType || null}
            exclusive
            onChange={(_, value) => handleSessionUserTypeChange(value || "")}
            fullWidth
            size="small"
            sx={{ mt: 0.5 }}
          >
            <ToggleButton value="" aria-label="전체">
              전체
            </ToggleButton>
            <ToggleButton value="GUEST" aria-label="게스트">
              게스트
            </ToggleButton>
            <ToggleButton value="MEMBER" aria-label="회원">
              회원
            </ToggleButton>
            <ToggleButton value="ADMIN" aria-label="관리자">
              관리자
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {/* 상태 */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" color="text.secondary" gutterBottom>
            세션 상태
          </Typography>
          <FormControl fullWidth variant="outlined" size="small">
            <Select
              value={formValues.status || ""}
              onChange={(event) => {
                handleStatusChange(event.target.value);
              }}
              displayEmpty
              sx={{ mt: 0.5 }}
            >
              <MenuItem value="">
                <ListItemText primary="전체" />
              </MenuItem>
              {statusOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  <ListItemText primary={option.label} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* 날짜 범위 */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" color="text.secondary" gutterBottom>
            날짜 범위
          </Typography>
          <Box sx={{ display: "flex", gap: 1, mt: 0.5 }}>
            <TextField
              type="date"
              size="small"
              value={formValues.startDate}
              onChange={(e) => handleStartDateChange(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ flex: 1 }}
            />
            <Typography variant="body2" sx={{ alignSelf: "center", px: 1 }}>
              ~
            </Typography>
            <TextField
              type="date"
              size="small"
              value={formValues.endDate}
              onChange={(e) => handleEndDateChange(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ flex: 1 }}
            />
          </Box>
        </Box>

        {/* 피드백 필터 */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="caption" color="text.secondary" gutterBottom>
            피드백 상태
          </Typography>
          <ToggleButtonGroup
            value={formValues.feedbackFilter || null}
            exclusive
            onChange={(_, value) => handleFeedbackFilterChange(value || "")}
            fullWidth
            size="small"
            sx={{ mt: 0.5 }}
          >
            <ToggleButton value="" aria-label="전체">
              전체
            </ToggleButton>
            <ToggleButton value="has_feedback" aria-label="피드백 있음">
              피드백 있음
            </ToggleButton>
            <ToggleButton value="no_feedback" aria-label="피드백 없음">
              피드백 없음
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        <Box sx={{ display: "flex", justifyContent: "space-between", pt: 1 }}>
          <Button
            variant="outlined"
            size="small"
            onClick={handleReset}
            startIcon={<ResetIcon />}
          >
            초기화
          </Button>
          <Button
            variant="contained"
            disableElevation
            size="small"
            onClick={handleApplyFilters}
          >
            적용
          </Button>
        </Box>
      </Popover>
    </Paper>
  );
};
